import { useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"
import { useMemo, useState } from "react"

interface AssignmentData {
  equipment_id: number | null
  user_id: number | null
  assigned_at: string | null
  start_odometer: number | null
  notes: string
  install_vehicle: string
  install_vehicle_location: string
  install_gps_location: string
}

interface EquipmentType {
  id: number
  name: string
  requires_location: boolean
}

interface EquipmentUnit {
  id: number
  name: string
  sort_name: string | null
  equipment_type_id: number | null
  equipment_type_name: string | null
  requires_odometer: boolean
  requires_location: boolean
  requires_vehicle: boolean
  current_assignee: string | null
}

interface User {
  id: number
  name: string
}

interface Props {
  caseId: number
  assignment: AssignmentData
  equipmentTypes: EquipmentType[]
  equipmentUnits: EquipmentUnit[]
  users: User[]
  errors?: Record<string, string[]>
  onCancel: () => void
}

export default function EquipmentAssignmentForm({
  caseId,
  assignment,
  equipmentTypes,
  equipmentUnits,
  users,
  errors,
  onCancel,
}: Props) {
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null)

  const { data, setData, post, processing, transform } =
    useForm<AssignmentData>({
      equipment_id: assignment.equipment_id,
      user_id: assignment.user_id,
      assigned_at: assignment.assigned_at,
      start_odometer: assignment.start_odometer,
      notes: assignment.notes,
      install_vehicle: assignment.install_vehicle,
      install_vehicle_location: assignment.install_vehicle_location,
      install_gps_location: assignment.install_gps_location,
    })

  transform((data) => ({ equipment_assignment: data }))

  // Filter equipment units by selected type
  const filteredUnits = useMemo(() => {
    if (!selectedTypeId) return equipmentUnits
    return equipmentUnits.filter((u) => u.equipment_type_id === selectedTypeId)
  }, [selectedTypeId, equipmentUnits])

  // Determine conditional field requirements based on selected equipment
  const selectedUnit = useMemo(
    () => equipmentUnits.find((u) => u.id === data.equipment_id),
    [data.equipment_id, equipmentUnits],
  )

  const requiresOdometer = selectedUnit?.requires_odometer ?? false
  const requiresLocation = selectedUnit?.requires_location ?? false
  const requiresVehicle = selectedUnit?.requires_vehicle ?? false

  const handleTypeFilter = (typeId: number | null) => {
    setSelectedTypeId(typeId)
    // Clear equipment selection when type filter changes
    setData("equipment_id", null)
  }

  const handleEquipmentChange = (equipmentId: number | null) => {
    setData("equipment_id", equipmentId)
    // Auto-set type filter if a unit is selected
    if (equipmentId) {
      const unit = equipmentUnits.find((u) => u.id === equipmentId)
      if (unit?.equipment_type_id) {
        setSelectedTypeId(unit.equipment_type_id)
      }
    }
  }

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    post(`/cases/${caseId}/equipment_assignments`)
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* Equipment Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Equipment Selection</h3>

        <div>
          <label className={labelClass}>Equipment Type (filter)</label>
          <select
            className={inputClass}
            value={selectedTypeId ?? ""}
            onChange={(e) =>
              handleTypeFilter(e.target.value ? parseInt(e.target.value) : null)
            }
          >
            <option value="">All types</option>
            {equipmentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Equipment Unit</label>
          <select
            className={inputClass}
            value={data.equipment_id ?? ""}
            onChange={(e) =>
              handleEquipmentChange(
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            required
          >
            <option value="">Select equipment...</option>
            {filteredUnits.map((unit) => {
              const label = unit.equipment_type_name
                ? `${unit.equipment_type_name}: ${unit.name}`
                : unit.name
              const assignee = unit.current_assignee
                ? ` (Assigned to: ${unit.current_assignee})`
                : ""
              return (
                <option key={unit.id} value={unit.id}>
                  {label}
                  {assignee}
                </option>
              )
            })}
          </select>
          {errors?.equipment_id && (
            <p className={errorClass}>{errors.equipment_id[0]}</p>
          )}
          {errors?.equipment && (
            <p className={errorClass}>{errors.equipment[0]}</p>
          )}
        </div>
      </div>

      {/* Assignment Details */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Assignment Details</h3>

          <div>
            <label className={labelClass}>Assigned To / Installed By</label>
            <select
              className={inputClass}
              value={data.user_id ?? ""}
              onChange={(e) =>
                setData(
                  "user_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              required
            >
              <option value="">Select employee...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            {errors?.user_id && (
              <p className={errorClass}>{errors.user_id[0]}</p>
            )}
            {errors?.user && <p className={errorClass}>{errors.user[0]}</p>}
          </div>

          <div>
            <label className={labelClass}>Assign / Install Date & Time</label>
            <input
              type="datetime-local"
              className={inputClass}
              value={data.assigned_at ?? ""}
              onChange={(e) => setData("assigned_at", e.target.value)}
              required
            />
            {errors?.assigned_at && (
              <p className={errorClass}>{errors.assigned_at[0]}</p>
            )}
          </div>
        </div>

        {/* Conditional Fields */}
        <div className="space-y-4">
          {/* Vehicle fields - shown only for GPS type equipment */}
          {requiresVehicle && (
            <>
              <h3 className="text-lg font-medium">Vehicle Details</h3>
              <div>
                <label className={labelClass}>Vehicle</label>
                <input
                  type="text"
                  className={inputClass}
                  value={data.install_vehicle}
                  onChange={(e) => setData("install_vehicle", e.target.value)}
                  placeholder="Vehicle description"
                  required
                />
                {errors?.install_vehicle && (
                  <p className={errorClass}>{errors.install_vehicle[0]}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>
                  Location of Vehicle When Device Installed
                </label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={data.install_vehicle_location}
                  onChange={(e) =>
                    setData("install_vehicle_location", e.target.value)
                  }
                  placeholder="Address or description of location"
                  required
                />
                {errors?.install_vehicle_location && (
                  <p className={errorClass}>
                    {errors.install_vehicle_location[0]}
                  </p>
                )}
              </div>
            </>
          )}
          {/* Device Placement - shown for all location-requiring equipment types */}
          {requiresLocation && (
            <>
              <h3 className="text-lg font-medium">Device Placement</h3>
              <div>
                <label className={labelClass}>Device Placement</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={data.install_gps_location}
                  onChange={(e) =>
                    setData("install_gps_location", e.target.value)
                  }
                  placeholder="Where device is placed"
                  required
                />
                {errors?.install_gps_location && (
                  <p className={errorClass}>{errors.install_gps_location[0]}</p>
                )}
              </div>
            </>
          )}

          {/* Odometer fields - shown when equipment requires odometer */}
          {requiresOdometer && (
            <>
              <h3 className="text-lg font-medium">Odometer / Hours</h3>
              <div>
                <label className={labelClass}>Start Odometer / Hours</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  value={data.start_odometer ?? ""}
                  onChange={(e) =>
                    setData(
                      "start_odometer",
                      e.target.value ? parseFloat(e.target.value) : null,
                    )
                  }
                  required={requiresOdometer}
                />
                {errors?.start_odometer && (
                  <p className={errorClass}>{errors.start_odometer[0]}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className={labelClass}>Objectives / Notes</label>
        <textarea
          className={inputClass}
          rows={3}
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {processing ? "Saving..." : "Assign Equipment"}
        </button>
      </div>
    </form>
  )
}
